'use client';

import { useEffect, useState } from 'react';

import { TabsType } from './use-schedule-tab.hook';
import { useGridCol } from './use-grid-col.hook';
import { useSearchFilter } from './use-search-filters.hook';

import { getSchedulesSlotById } from '@/app/(main)/(pages)/_components/schedules/_services/get-schedules-slot-by-id.service';
import { useLoading } from '@/hooks/use-loading.hook';
import { SchedulesSearchSlot, ScheduleType, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { groupByDateToTimeGrid } from '@/app/(main)/(pages)/busca/[slug]/_consts';
import { toSearchApiDayTime } from '@/app/(main)/(pages)/busca/[slug]/_utils/search-day-time-api.util';

export interface UseTimeSlotsProps {
  selectedTab: TabsType;
  schedule: SearchPartnerData;
}

export interface UseTimeSlotsReturn {
  isFirstTabLoading: boolean;
  fetchNewSlots(): void;
  fetchSlots(id: string, type: ScheduleType): void;
  scheduleSlots: SchedulesSearchSlot[] | undefined;
  arrivalOrder: SchedulesSearchSlot[] | undefined;
  laboratorial: SchedulesSearchSlot[] | undefined;
  hasEndedSchedulesSlots: boolean;
  hasEndedArrivalOrder: boolean;
  hasEndedLaboratorialSlots: boolean;
}

export const useTimeSlots = ({ selectedTab, schedule }: UseTimeSlotsProps): UseTimeSlotsReturn => {
  const { filters } = useSearchFilter();
  const { dayTime, minDate, maxDate } = filters;

  const [scheduleSlots, setScheduleSlots] = useState<SchedulesSearchSlot[]>();
  const [arrivalOrder, setArrivalOrder] = useState<SchedulesSearchSlot[]>();
  const [laboratorial, setLaboratorial] = useState<SchedulesSearchSlot[]>();
  const [hasEndedSchedulesSlots, setHasEndedSchedulesSlots] = useState(false);
  const [hasEndedArrivalOrder, setHasEndedArrivalOrder] = useState(false);
  const [hasEndedLaboratorialSlots, setHasEndedLaboratorialSlots] = useState(false);

  const { isLoading: isFirstTabLoading, startLoading, stopLoading } = useLoading();
  const [isFetchingNext, setIsFetchingNext] = useState(false);

  const tabToScheduleType: Partial<Record<TabsType, ScheduleType>> = {
    [TabsType.SCHEDULED]: ScheduleType.SCHEDULED,
    [TabsType.ARRIVAL_ORDER]: ScheduleType.ARRIVAL_ORDER,
    [TabsType.LABORATORIAL]: ScheduleType.LABORATORIAL,
  };

  const scheduleType = tabToScheduleType[selectedTab] || ScheduleType.ARRIVAL_ORDER;

  const cols = useGridCol({ scheduleType });

  async function fetchSlots(id: string, type: ScheduleType) {
    startLoading();
    const { slots, hasMore } = await getSchedulesSlotById({
      id,
      limitDays: cols,
      offsetDays: 0,
      dayTime: toSearchApiDayTime(dayTime),
      minDate,
      maxDate,
    });

    if (type === ScheduleType.SCHEDULED) {
      setScheduleSlots(slots);
      setHasEndedSchedulesSlots(!hasMore);
    }

    if (type === ScheduleType.ARRIVAL_ORDER) {
      setArrivalOrder(slots);
      setHasEndedArrivalOrder(!hasMore);
    }

    if (type === ScheduleType.LABORATORIAL) {
      setLaboratorial(slots);
      setHasEndedLaboratorialSlots(!hasMore);
    }
    stopLoading();
  }

  useEffect(() => {
    if (cols) {
      setScheduleSlots(undefined);
      setArrivalOrder(undefined);
      setLaboratorial(undefined);
      setHasEndedSchedulesSlots(false);
      setHasEndedArrivalOrder(false);
      setHasEndedLaboratorialSlots(false);
    }
  }, [cols, dayTime, minDate, maxDate]);

  useEffect(() => {
    const scheduleTypesMap: Partial<Record<TabsType, { state: SchedulesSearchSlot[] | undefined; type: ScheduleType }>>
      = {
        [TabsType.SCHEDULED]: {
          state: scheduleSlots,
          type: ScheduleType.SCHEDULED,
        },
        [TabsType.ARRIVAL_ORDER]: {
          state: arrivalOrder,
          type: ScheduleType.ARRIVAL_ORDER,
        },
        [TabsType.LABORATORIAL]: {
          state: laboratorial,
          type: ScheduleType.LABORATORIAL,
        },
      };

    const selectedSchedule = scheduleTypesMap[selectedTab];

    if (selectedSchedule && !selectedSchedule.state) {
      const scheduleType = schedule?.schedulesTypes[selectedSchedule.type];
      if (scheduleType?.length) {
        fetchSlots(scheduleType[0]?.id, selectedSchedule.type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, schedule, scheduleSlots, arrivalOrder, laboratorial]);

  const fetchSlotsForTab = async (
    state: SchedulesSearchSlot[] | undefined,
    setState: React.Dispatch<React.SetStateAction<SchedulesSearchSlot[] | undefined>>,
    setHasEnded: React.Dispatch<React.SetStateAction<boolean>>,
    hasEnded: boolean,
    scheduleType: ScheduleType,
  ) => {
    if (hasEnded) {
      return;
    }

    const scheduleTypeData = schedule?.schedulesTypes[scheduleType];
    const offSet = groupByDateToTimeGrid(state).length;

    const { slots, hasMore } = await getSchedulesSlotById({
      id: scheduleTypeData[0]?.id,
      limitDays: cols,
      offsetDays: offSet,
      dayTime: toSearchApiDayTime(dayTime),
      minDate,
      maxDate,
    });

    setHasEnded(!hasMore || slots.length === 0);

    if (slots.length) {
      setState((old) => [...(old ?? []), ...slots]);
    }
  };

  const fetchNewSlots = async () => {
    if (isFetchingNext) {
      return;
    }
    setIsFetchingNext(true);

    if (selectedTab === TabsType.SCHEDULED) {
      await fetchSlotsForTab(
        scheduleSlots,
        setScheduleSlots,
        setHasEndedSchedulesSlots,
        hasEndedSchedulesSlots,
        ScheduleType.SCHEDULED,
      );
    }

    if (selectedTab === TabsType.ARRIVAL_ORDER) {
      await fetchSlotsForTab(
        arrivalOrder,
        setArrivalOrder,
        setHasEndedArrivalOrder,
        hasEndedArrivalOrder,
        ScheduleType.ARRIVAL_ORDER,
      );
    }

    if (selectedTab === TabsType.LABORATORIAL) {
      await fetchSlotsForTab(
        laboratorial,
        setLaboratorial,
        setHasEndedLaboratorialSlots,
        hasEndedLaboratorialSlots,
        ScheduleType.LABORATORIAL,
      );
    }
    setIsFetchingNext(false);
  };

  return {
    isFirstTabLoading,
    scheduleSlots,
    arrivalOrder,
    laboratorial,
    hasEndedSchedulesSlots,
    hasEndedArrivalOrder,
    hasEndedLaboratorialSlots,
    fetchNewSlots,
    fetchSlots,
  };
};
